import clsx from 'clsx'
import { rem } from 'polished'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

import FlashHotelDesktopSameRoomOptions from './FlashHotelDesktopSameRoomOptions'

import LayoutContainer from 'components/Common/LayoutContainer'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Backdrop from 'components/utils/Backdrop'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { mediaQueryDown } from 'components/utils/breakpoint'
import config from 'constants/config'
import type { OfferPageFilterType, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { isFlightForceBundled } from 'lib/offer/isFlightForceBundled'
import OfferCalendarDatePickerSelect from '../Filters/CalendarPicker/OfferCalendarDatePickerSelect'
import OfferCapacityWithAgesSelect from '../Filters/CapacityWithAges/OfferCapacityWithAgesSelect'
import type { OfferDurationOption } from '../Filters/DurationTabs/OfferDurationTabs'
import OfferDurationTabs from '../Filters/DurationTabs/OfferDurationTabs'
import FlashHotelDesktopIndividualRooms from './FlashHotelDesktopIndividualRooms'
import WalledContent from 'components/Common/WalledContent'
import {
  getCalendarDurationOptionsWithCharterBundleRestrictions,
  getCanUseMultiRoomUI, getCheckoutDate,
  getEffectiveCharterBundleRoute, isAvailableForPurchase,
  offerFlightsEnabled,
} from 'selectors/offerPage/offerPageSelectors'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import HotelDesktopFlightToggle from '../Accommodation/HotelDesktopFlightToggle'
import MultiRoomModeToggle from '../MultipleRoomsBooking/MultiRoomModeToggle'
import OfferDesktopConnectingRoomsToggle from '../Filters/ConnectingRooms/OfferDesktopConnectingRoomsToggle'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'
import { NON_CHARTER_TO_CHARTER_OFFER_MAP } from 'constants/charterFlight'
import useCharterFlightLabels from 'hooks/Flights/useCharterFlightLabels'
import CharterBundleBanner from '../CharterBundle/CharterBundleBanner'
import ReturnCharterFlightTile from 'components/Flights/CharterBundle/ReturnCharterFlightTile'
import CharterFlightOfferAirportSelect from '../Filters/CharterFlight/CharterFlightOfferAirportSelect'
import CharterFlightFareClassSelect from '../Filters/CharterFlight/CharterFlightFareClassSelect'
import CharterFlightCalendarSelect from '../Filters/CharterFlight/CharterFlightCalendarSelect'
import type { FlightsClassTypes } from 'constants/flight'
import Caption from 'LuxKit/Typography/Caption'
import moment from 'moment'
import PriceModeSelectDesktop from 'components/OfferPage/Filters/PriceModeSelect/PriceModeSelectDesktop'
import Switch from 'LuxKit/Switch/Switch'
import { OfferFilterSelectButton, OfferFilterSelectButtonContent } from 'components/OfferPage/Filters/OfferFilterSelectButton'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import PageBanner from 'LuxKit/Banners/PageBanner'
import LineExclamationTriangleIcon from 'LuxKit/Icons/line/LineExclamationTriangleIcon'
import NestableClickable from 'components/Common/NestableClickable'
import type { DateRange } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/types'
import { useIntl } from 'react-intl'

const DesktopFilterBlock = styled(Group)`
  &.open {
    position: relative;
    z-index: 3;
  }
`

const StyledOfferCapacityWithAgesSelect = styled(OfferCapacityWithAgesSelect)`
  flex-grow: 1;
  max-width: ${rem(250)};
`

const StyledOfferCalendarDatePickerSelect = styled(OfferCalendarDatePickerSelect)`
  flex-grow: 1;
  max-width: ${rem(250)};
`

const FlexDesktopFilterBlock = styled(DesktopFilterBlock)`
  flex-grow: 1;

  &:has(> :nth-child(3)) > * {
    flex: 1;
    max-width: unset;
  }

  ${mediaQueryDown.desktop} {
    &:has(> :nth-child(4)) > * {
      /* To ensure when they wrap we don't get three filters on the first row and then on on the second row */
      min-width: 34%;
    }
  }
`

interface Props {
  offer: App.HotelOffer
  airport?: App.Airport
  duration: number
  activeFilter?: OfferPageFilterType
  rooms: Array<OfferPageRoom>
  checkInDate?: string
  checkOutDate?: string
  availableForPurchase?: boolean
  flightsEnabled?: boolean
  multipleRoomBookingActive: boolean
  canUseMultiRoomUI: boolean
  charterBundleRoute?: App.CharterBundleRoute
  charterBundleEnabled: boolean
  fareClass: FlightsClassTypes
  effectiveCharterBundleRoute?: App.CharterBundleRoute
  calendarDurationOptions: Array<OfferDurationOption>
  isSpoofed: boolean
  setIsStickyFooterVisible: (isVisible: boolean) => void
  showFlightInformation: boolean
  charterDateRangesByMonth: Record<string, Array<DateRange>>
  cheapestCharterDateRange?: DateRange
  onCharterDurationChange: (nights: number) => void
  occupancyLimits: Array<App.RoomCapacity>
  destinationAirport?: App.Airport
  desktopFilterBlockRef: React.RefObject<HTMLDivElement | null>
}

const CHARTER_UNBUNDLED_NOTICE_LINE_ONE = 'This offer must be bundled with a Maldives Charter flight via Add To Cart. It cannot be purchased without an LE Charter flight.'
const CHARTER_UNBUNDLED_NOTICE_LINE_TWO = 'Hotel-only bookings must be combined with another hotel stay as a 5+7, 6+6, or 7+5 nights combo.'

function FlashHotelDesktopRoomOptions(props: Props) {
  const {
    offer,
    airport,
    duration,
    activeFilter,
    rooms,
    checkInDate,
    checkOutDate,
    availableForPurchase,
    flightsEnabled,
    multipleRoomBookingActive,
    canUseMultiRoomUI,
    charterBundleRoute,
    charterBundleEnabled,
    fareClass,
    effectiveCharterBundleRoute,
    calendarDurationOptions,
    isSpoofed,
    setIsStickyFooterVisible,
    showFlightInformation,
    charterDateRangesByMonth,
    cheapestCharterDateRange,
    onCharterDurationChange,
    occupancyLimits,
    destinationAirport,
    desktopFilterBlockRef,
  } = props

  const intl = useIntl()
  const { charterBundleDurationOptions } = useCharterFlightLabels()
  const isHotelOnlyModeOnCharterOffer = !!charterBundleRoute && !charterBundleEnabled

  const stateDispatch = useOfferPageDispatch()
  const desktopFiltersWrapperRef = useRef<HTMLDivElement>(null)
  // Charter calendar UI state
  const [selectedMonth, setSelectedMonth] = useState<string>(
    checkInDate ? moment(checkInDate).format('MMM') : moment().format('MMM'),
  )

  useEffect(() => {
    if (!checkInDate && cheapestCharterDateRange?.checkIn) {
      setSelectedMonth(moment(cheapestCharterDateRange.checkIn).format('MMM'))
    }
  }, [checkInDate, cheapestCharterDateRange?.checkIn])

  useEffect(() => {
    const months = Object.keys(charterDateRangesByMonth)
    if (months.length > 0 && !months.some(month => month.toLowerCase() === selectedMonth.toLowerCase())) {
      setSelectedMonth(months[0])
    }
  }, [charterDateRangesByMonth, selectedMonth])

  const flightTileCheckIn = checkInDate || cheapestCharterDateRange?.checkIn
  const flightTileCheckOut = checkOutDate || cheapestCharterDateRange?.checkOut

  const handleCharterBundleToggle = useCallback(() => {
    stateDispatch({
      type: OfferPageActions.setCharterBundleEnabled,
      charterBundleEnabled: !charterBundleEnabled,
    })
  }, [charterBundleEnabled, stateDispatch])

  const charterBundleToggleValue = charterBundleEnabled
    ? intl.formatMessage({
        id: 'ecxcPGUQ4s',
        defaultMessage: 'Included in package',
        description: 'Text indicating charter flights are included in package',
      })
    : intl.formatMessage({
        id: 'bZVyK8s74g',
        defaultMessage: 'Hotel only',
        description: 'Text indicating hotel only option',
      })

  return (
    <VerticalSpacer gap={32}>
      <LayoutContainer>
        <VerticalSpacer gap={32}>
          {availableForPurchase && <WalledContent enforced={offer.walledGarden}>
            <CSSBreakpoint min="tablet">
              <ScrollAnchor ref={desktopFilterBlockRef} vOffset={rem(12)} />
              <FlexDesktopFilterBlock direction="horizontal" gap={8} horizontalAlign="center" wrap="wrap" className={clsx({ open: !!activeFilter })}>
                <StyledOfferCapacityWithAgesSelect
                  boundaryRef={desktopFiltersWrapperRef}
                  capacities={occupancyLimits}
                  disableMultiRoom={isFlightForceBundled(offer)}
                />
                {charterBundleRoute && isSpoofed && (
                  <OfferFilterSelectButton
                    as={NestableClickable}
                    onClick={handleCharterBundleToggle}
                    data-testid="charter-bundle-toggle"
                  >
                    <LinePlaneDepartureIcon />
                    <OfferFilterSelectButtonContent>
                      <BodyText variant="medium" wrap="truncate">
                        {intl.formatMessage({
                          id: '4TBAoReIZ6',
                          defaultMessage: 'Charter flights',
                          description: 'Label for charter flights filter',
                        })}
                      </BodyText>
                      <Caption variant="medium" colour="neutral-three" wrap="truncate">
                        {charterBundleToggleValue}
                      </Caption>
                    </OfferFilterSelectButtonContent>
                    <Switch
                      checked={charterBundleEnabled}
                      onChange={handleCharterBundleToggle}
                    />
                  </OfferFilterSelectButton>
                )}
                {effectiveCharterBundleRoute && <>
                  <CharterFlightOfferAirportSelect />
                  <CharterFlightFareClassSelect />
                  <div>
                    <CharterFlightCalendarSelect
                      selectedMonth={selectedMonth}
                      onMonthChange={setSelectedMonth}
                      nights={duration}
                      fareClass={fareClass}
                      onDurationChange={onCharterDurationChange}
                      durationOptions={charterBundleDurationOptions}
                      checkInDate={checkInDate}
                      charterBundleRoute={effectiveCharterBundleRoute}
                      rooms={rooms}
                      dateRangesByMonth={charterDateRangesByMonth}
                    />
                  </div>
                </>}
                {!effectiveCharterBundleRoute && <StyledOfferCalendarDatePickerSelect
                  boundaryRef={desktopFiltersWrapperRef}
                />}
                {rooms.length > 1 && !effectiveCharterBundleRoute && <OfferDesktopConnectingRoomsToggle />}
                {flightsEnabled && !effectiveCharterBundleRoute && <HotelDesktopFlightToggle />}
                <Backdrop visible={!!activeFilter} data-testid="filter-backdrop" />
              </FlexDesktopFilterBlock>
            </CSSBreakpoint>
          </WalledContent>}
          {effectiveCharterBundleRoute && <ReturnCharterFlightTile
            route={effectiveCharterBundleRoute}
            occupancies={rooms}
            checkInDate={flightTileCheckIn}
            checkOutDate={flightTileCheckOut}
            fareClass={fareClass}
            flightOrigin={airport}
          />}
          {isHotelOnlyModeOnCharterOffer && (
            <PageBanner kind="warning" icon={<LineExclamationTriangleIcon />}>
              <>
                {CHARTER_UNBUNDLED_NOTICE_LINE_ONE}
                <br />
                {CHARTER_UNBUNDLED_NOTICE_LINE_TWO}
              </>
            </PageBanner>
          )}
          {!effectiveCharterBundleRoute && <VerticalSpacer gap={16}>
            <VerticalSpacer gap={8}>
              <BodyText variant="large" align="center">
                {intl.formatMessage({
                  id: 'HpyH065R/h',
                  defaultMessage: 'Select duration',
                  description: 'Heading for duration selection',
                })}
              </BodyText>
              <OfferDurationTabs durationOptionsOverride={isHotelOnlyModeOnCharterOffer ? calendarDurationOptions : undefined} />
            </VerticalSpacer>
            {NON_CHARTER_TO_CHARTER_OFFER_MAP[offer.id] && (
              <CharterBundleBanner offer={offer} />
            )}
          </VerticalSpacer>}
          <VerticalSpacer gap={16}>
            <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="start" gap={16}>
              <div>
                <Heading variant="heading3" as="h2">
                  {intl.formatMessage(
                    {
                      id: 'nJlPZHrlHo',
                      defaultMessage: 'Select your {count, plural, one {room} other {# rooms}}',
                      description: 'Heading for selecting room packages',
                    },
                    { count: rooms.length },
                  )}
                </Heading>
                {canUseMultiRoomUI && <BodyText variant="medium">
                  {intl.formatMessage({
                    id: 'fwqPvhfaEG',
                    defaultMessage: 'Choose the same package for all rooms, or customise each room individually.',
                    description: 'Description for multi-room booking options',
                  })}
                </BodyText>}
              </div>
              {!!charterBundleRoute && <PriceModeSelectDesktop />}
            </Group>
            {canUseMultiRoomUI && <Group direction="horizontal"><MultiRoomModeToggle inputsName="room-mode-desktop" /></Group>}
          </VerticalSpacer>
        </VerticalSpacer>
      </LayoutContainer>
      {!multipleRoomBookingActive && offer.packages.length > 0 && <FlashHotelDesktopSameRoomOptions
        destinationAirport={destinationAirport}
        onStickyFooterToggle={setIsStickyFooterVisible}
      />}
      {multipleRoomBookingActive && <FlashHotelDesktopIndividualRooms />}
      {showFlightInformation && (
        <LayoutContainer>
          <Caption variant="medium" colour="neutral-two" align="center">
            {intl.formatMessage(
              {
                id: 'YDD1yHaoF6',
                defaultMessage: '*Price is indicative and based on sample return fares (inclusive of booking fees on Low Cost Carriers). To view live pricing, conduct a full search. {brandName} full Terms and Conditions apply.',
                description: 'Pricing disclaimer shown below hotel room options when flights are enabled',
              },
              { brandName: config.brandConfig.name },
            )}
          </Caption>
        </LayoutContainer>
      )}
    </VerticalSpacer>
  )
}

type MappedProps = Pick<Props,
  | 'airport'
  | 'duration'
  | 'activeFilter'
  | 'rooms'
  | 'checkInDate'
  | 'checkOutDate'
  | 'availableForPurchase'
  | 'flightsEnabled'
  | 'multipleRoomBookingActive'
  | 'canUseMultiRoomUI'
  | 'charterBundleRoute'
  | 'charterBundleEnabled'
  | 'fareClass'
  | 'effectiveCharterBundleRoute'
  | 'calendarDurationOptions'
  | 'isSpoofed'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  airport: state.airport,
  duration: state.duration,
  activeFilter: state.activeFilter,
  rooms: state.rooms,
  checkInDate: state.checkInDate,
  checkOutDate: getCheckoutDate(state),
  flightsEnabled: offerFlightsEnabled(state, appState),
  availableForPurchase: isAvailableForPurchase(state, appState),
  multipleRoomBookingActive: isMultipleRoomBookingActive(state, appState),
  canUseMultiRoomUI: getCanUseMultiRoomUI(state, appState),
  charterBundleRoute: state.charterBundleRoute,
  charterBundleEnabled: state.charterBundleEnabled,
  fareClass: state.fareClass,
  effectiveCharterBundleRoute: getEffectiveCharterBundleRoute(state),
  calendarDurationOptions: getCalendarDurationOptionsWithCharterBundleRestrictions(state, appState),
  isSpoofed: appState.auth.account.isSpoofed,
}), FlashHotelDesktopRoomOptions)
